import React from 'react';
import SiteHeader from '../../components/SiteHeader';
import SiteFooter from '../../components/SiteFooter';
import { Building2, TreeDeciduous, Trophy, Users, Target, Award, CheckCircle2 } from 'lucide-react';

const GolfAboutPage = () => {
  const values = [
    {
      icon: Trophy,
      title: "Excellence Sportive",
      description: "Un parcours aux standards internationaux, conçu pour offrir une expérience de jeu exceptionnelle."
    },
    {
      icon: TreeDeciduous,
      title: "Respect de l'Environnement",
      description: "Une intégration harmonieuse dans le paysage naturel, préservant les écosystèmes locaux."
    },
    {
      icon: Users,
      title: "Convivialité",
      description: "Un lieu de rencontre et de partage pour une communauté passionnée de golf."
    },
    {
      icon: Target,
      title: "Développement Territorial",
      description: "Un équipement structurant qui contribue au rayonnement de la région de Songon."
    }
  ];

  const timeline = [
    { year: "2023", event: "Lancement du projet et études de faisabilité" },
    { year: "2024", event: "Validation du masterplan et début des travaux" },
    { year: "2025", event: "Construction du parcours et du clubhouse" },
    { year: "2027", event: "Ouverture prévue au public" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-emerald-900">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }} />
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <h1 className="text-4xl lg:text-5xl font-light text-white mb-6">
                À propos du Golf de Songon M'braté
              </h1>
              <p className="text-xl text-emerald-100/90 leading-relaxed">
                Découvrez l'histoire, la vision et les valeurs qui animent le projet 
                du Golf de Songon M'braté, un parcours d'exception au cœur de la Côte d'Ivoire.
              </p>
            </div>
          </div>
        </section>

        {/* Vision Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-6">
                  Notre Vision
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Le Golf de Songon M'braté M'braté est bien plus qu'un simple parcours de golf. C'est une vision 
                  ambitieuse qui vise à créer un lieu d'exception où sport, nature et communauté 
                  se rencontrent harmonieusement.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Conçu comme un équipement structurant pour le territoire de Songon, notre golf 
                  contribuera au développement économique local tout en offrant aux joueurs une 
                  expérience de jeu inégalée en Afrique de l'Ouest.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Notre ambition : devenir une référence en matière de golf durable et responsable, 
                  alliant excellence sportive et respect de l'environnement.
                </p>
              </div>
              <div className="relative">
                <img
                  src="https://images.pexels.com/photos/914682/pexels-photo-914682.jpeg"
                  alt="Vision du Golf"
                  className="w-full h-[400px] object-cover rounded-2xl shadow-xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-emerald-700 text-white p-6 rounded-xl shadow-lg">
                  <div className="text-4xl font-light mb-1">18</div>
                  <div className="text-emerald-200 text-sm">Trous Championship</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-stone-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-6">
                Nos Valeurs
              </h2>
              <p className="text-xl text-gray-600">
                Les principes qui guident notre projet au quotidien.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all duration-300">
                  <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-6">
                    <value.icon className="w-7 h-7 text-emerald-700" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-6">
                Notre Parcours
              </h2>
              <p className="text-xl text-gray-600">
                Les grandes étapes du projet Golf de Songon M'braté.
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              {timeline.map((item, index) => (
                <div key={index} className="flex gap-6 mb-8 last:mb-0">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-emerald-700 rounded-full flex items-center justify-center text-white font-semibold">
                      {item.year.slice(-2)}
                    </div>
                    {index < timeline.length - 1 && (
                      <div className="w-0.5 flex-1 bg-emerald-200 mt-2" />
                    )}
                  </div>
                  <div className="flex-1 pb-8">
                    <div className="text-emerald-700 font-semibold mb-1">{item.year}</div>
                    <p className="text-gray-600">{item.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* One Green Dev Section */}
        <section className="py-20 bg-emerald-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl lg:text-4xl font-light text-white mb-6">
                Un projet One Green Dev
              </h2>
              <p className="text-xl text-emerald-100/90 mb-8">
                Le Golf de Songon M'braté M'braté fait partie intégrante de l'écosystème One Green Dev, 
                un développeur territorial qui conçoit des projets intégrés alliant golf, 
                habitat et foncier stratégique.
              </p>
              <a
                href="https://onegreendev.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-emerald-900 px-8 py-4 rounded-lg font-semibold hover:bg-emerald-50 transition-colors"
              >
                Découvrir One Green Dev
              </a>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default GolfAboutPage;
